/**
 * Manage Appointments Service
 *
 * Service layer for fetching the user's appointments list and cancel-flow APIs.
 */

import type { AxiosRequestConfig } from "axios";
import apiService from "@/services/api";
import type {
  ManageAppointmentListResponse,
  ManageAppointmentListMeta,
  ManageAppointmentItem,
  CheckCanCancelResult,
  CancelAppointmentPayload,
  CancelAppointmentResult,
  CancellationReasonOption,
  OtpSendResponse,
  OtpVerifyResponse,
  TrackApplicationStatusPayload,
  TrackApplicationStatusResult,
} from "./types";

export type { ManageAppointmentItem, ManageAppointmentListMeta } from "./types";

export interface GetManageAppointmentsParams {
  page: number;
  limit: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface ManageAppointmentsListResult {
  data: ManageAppointmentItem[];
  meta: ManageAppointmentListMeta;
}

const APPOINTMENT_OTP_PURPOSE_CANCELLATION = "CANCELLATION" as const;

/** Reference-data response shape for a single source */
interface ReferenceDataSourceResponse {
  success?: boolean;
  data?: Record<string, Array<{ id: number; value: string; label: string }>>;
  message?: string;
}

/**
 * GET /v1/appointments/manage
 * Returns paginated list of user's appointments for the Manage Appointment screen.
 */
export const manageAppointmentsService = {
  getManageAppointments: async (
    params: GetManageAppointmentsParams
  ): Promise<ManageAppointmentsListResult> => {
    const response = await apiService.get<ManageAppointmentListResponse>(
      "/appointments/manage",
      { params }
    );
    return response.data;
  },

  /**
   * DELETE /v1/appointments/:id
   * Soft-delete a draft appointment. Only allowed when status is DRAFT. Returns 204 No Content.
   */
  deleteDraftAppointment: async (appointmentId: number): Promise<void> => {
    await apiService.delete(`/appointments/${appointmentId}`);
  },

  /**
   * GET /v1/appointments/checklist?countryId=&visaTypeId=&visaSubTypeId=&centerId=&centerType=
   * Returns the checklist document URL for the given country and visa type/sub type.
   * Pass `centerId` + `centerType` together to fetch a center-specific checklist
   */
  getChecklist: async (
    countryId: number,
    visaTypeId: number,
    visaSubTypeId: number,
    centerId?: number,
    centerType?: "hub" | "spoke" | "sub_country_hub"
  ): Promise<{ checklistUrl: string }> => {
    const hasCenter =
      typeof centerId === "number" && centerId >= 1 && !!centerType;
    const response = await apiService.get<{ checklistUrl: string }>(
      "/appointments/checklist",
      {
        params: {
          countryId,
          visaTypeId,
          visaSubTypeId,
          ...(hasCenter ? { centerId, centerType } : {}),
        },
      }
    );
    return response.data;
  },

  /**
   * POST /v1/appointments/can-cancel
   * Check whether the appointment can be cancelled (ownership, email, status BOOKED, 48h rule).
   */
  checkCanCancel: async (
    appointmentNumber: string,
    email: string
  ): Promise<CheckCanCancelResult> => {
    const response = await apiService.post<CheckCanCancelResult>(
      "/appointments/can-cancel",
      { appointmentNumber, email }
    );
    return response.data;
  },

  /**
   * POST /v1/otp/appointment/send
   * Send cancellation OTP to user's email; sets appointment status to CANCELLATION_OTP_PENDING.
   * @param config - Optional Axios config (e.g. reCAPTCHA headers via `recaptchaHeaders(token)`).
   */
  sendCancellationOtp: async (
    appointmentId: number,
    config?: AxiosRequestConfig
  ): Promise<OtpSendResponse> => {
    const response = await apiService.post<OtpSendResponse>(
      "/otp/appointment/send",
      { appointmentId, otpPurpose: APPOINTMENT_OTP_PURPOSE_CANCELLATION },
      config
    );
    return response.data;
  },

  /**
   * POST /v1/otp/appointment/verify
   * Verify cancellation OTP; sets appointment status to CANCELLATION_OTP_VERIFIED.
   * @param config - Optional Axios config (e.g. reCAPTCHA headers via `recaptchaHeaders(token)`).
   */
  verifyCancellationOtp: async (
    appointmentId: number,
    otp: string,
    config?: AxiosRequestConfig
  ): Promise<OtpVerifyResponse> => {
    const response = await apiService.post<OtpVerifyResponse>(
      "/otp/appointment/verify",
      {
        appointmentId,
        otp,
        otpPurpose: APPOINTMENT_OTP_PURPOSE_CANCELLATION,
      },
      config
    );
    return response.data;
  },

  /**
   * POST /v1/appointments/:id/cancel
   * Cancel the appointment (requires status CANCELLATION_OTP_VERIFIED).
   */
  cancelAppointment: async (
    appointmentId: number,
    payload: CancelAppointmentPayload
  ): Promise<CancelAppointmentResult> => {
    const response = await apiService.post<CancelAppointmentResult>(
      `/appointments/${appointmentId}/cancel`,
      payload
    );
    return response.data;
  },

  /**
   * GET /v1/reference-data?sources=appointment_cancellation_reasons
   * Fetch cancellation reasons for the cancel flow.
   */
  getCancellationReasons: async (): Promise<CancellationReasonOption[]> => {
    const response = await apiService.get<ReferenceDataSourceResponse>(
      "/reference-data",
      { params: { sources: "appointment_cancellation_reasons" } }
    );
    const data = response.data?.data;
    if (!data?.appointment_cancellation_reasons) {
      return [];
    }
    return data.appointment_cancellation_reasons as CancellationReasonOption[];
  },

  /**
   * POST /v1/appointments/reprint-letter
   * Validate appointment number + email (with reCAPTCHA) and return appointment letter PDF.
   * @param config - Optional Axios config (e.g. reCAPTCHA headers via recaptchaHeaders(token)).
   */
  reprintAppointmentLetter: async (
    appointmentNumber: string,
    email: string,
    config?: AxiosRequestConfig
  ): Promise<{ blob: Blob; filename: string }> => {
    return apiService.postBlob(
      "/appointments/reprint-letter",
      { appointmentNumber, email },
      config
    );
  },

  /**
   * POST /v1/visa-application/track-status
   * Track application status by reference number and date of birth.
   * @param config - Optional Axios config (e.g. reCAPTCHA headers via recaptchaHeaders(token)).
   */
  trackApplicationStatus: async (
    payload: TrackApplicationStatusPayload,
    config?: AxiosRequestConfig
  ): Promise<TrackApplicationStatusResult> => {
    const response = await apiService.post<TrackApplicationStatusResult>(
      "/visa-application/track-status",
      payload,
      config
    );
    return response.data;
  },
};

// -- NEW FAMILY CASE --
/**
 * Get cancellable participants for an appointment
 * GET /appointments/:id/cancellable-participants
 */
export const getCancellableParticipants = (appointmentId: number) => {
  return apiService.get(`/appointments/${appointmentId}/cancellable-participants`);
};

/**
 * Cancel specific participants from an appointment
 * POST /appointments/:id/participants/cancel
 */
export const cancelParticipants = (
  appointmentId: number,
  payload: {
    participantProfileIds: number[];
    cancellationReasonId: number;
    cancellationAdditionalComments?: string;
  }
) => {
  return apiService.post(`/appointments/${appointmentId}/participants/cancel`, payload);
};
// -- NEW FAMILY CASE --